import { z } from 'zod';

/** Column definitions for ImportCSV - aligned with Add/Edit feedback screens */
export const feedbackImportColumns = [
  { id: 'customer', label: 'Customer', type: 'string' as const },
  { id: 'source', label: 'Source', type: 'string' as const },
  { id: 'contributor', label: 'Contributor', type: 'string' as const },
  { id: 'content', label: 'Content', type: 'string' as const, validators: [{ type: 'required' as const, message: 'Content is required' }] },
  { id: 'sentiment', label: 'Sentiment', type: 'string' as const },
  { id: 'status', label: 'Status', type: 'string' as const },
  { id: 'priority', label: 'Priority', type: 'number' as const },
  { id: 'product_area', label: 'Product area', type: 'string' as const },
  { id: 'rating', label: 'Rating', type: 'number' as const },
];

export const ideaImportColumns = [
  { id: 'title', label: 'Title', type: 'string' as const, validators: [{ type: 'required' as const, message: 'Title is required' }] },
  { id: 'description', label: 'Description', type: 'string' as const },
  { id: 'status', label: 'Status', type: 'string' as const },
  { id: 'priority', label: 'Priority', type: 'number' as const },
  { id: 'effort', label: 'Effort', type: 'number' as const },
  { id: 'impact', label: 'Impact', type: 'number' as const },
  { id: 'reach', label: 'Reach', type: 'number' as const },
  { id: 'confidence', label: 'Confidence', type: 'number' as const },
  { id: 'time_criticality', label: 'Time criticality', type: 'number' as const },
  { id: 'risk_reduction', label: 'Risk reduction', type: 'number' as const },
  { id: 'category', label: 'Category', type: 'string' as const },
  { id: 'assigned_to', label: 'Assigned to', type: 'string' as const },
  { id: 'target_quarter', label: 'Target quarter', type: 'string' as const },
];

/** Coerce string to number 1-5, default 3 (for ideas) */
const prioritySchema = z
  .union([z.string(), z.number()])
  .transform((v) => {
    const n = typeof v === 'string' ? parseInt(v, 10) || 3 : v;
    return Math.min(5, Math.max(1, Number.isNaN(n) ? 3 : n)) as 1 | 2 | 3 | 4 | 5;
  })
  .optional()
  .default(3);

/** Coerce string to number 1-5, optional, default 3 (for feedback import) */
const feedbackPrioritySchema = z
  .union([z.string(), z.number()])
  .optional()
  .transform((v) => {
    if (v === undefined || v === null || v === '') return 3;
    const n = typeof v === 'string' ? parseInt(v, 10) : Number(v);
    return Math.min(5, Math.max(1, Number.isNaN(n) ? 3 : n)) as 1 | 2 | 3 | 4 | 5;
  })
  .default(3);

/** Map common sentiment values to positive/neutral/negative */
const sentimentSchema = z
  .string()
  .optional()
  .transform((v) => {
    if (!v || typeof v !== 'string') return 'neutral';
    const s = v.trim().toLowerCase();
    if (['positive', 'pos', '+', 'good', 'happy'].includes(s)) return 'positive';
    if (['negative', 'neg', '-', 'bad', 'unhappy'].includes(s)) return 'negative';
    return 'neutral';
  })
  .default('neutral');

/** Coerce to number in range 1-5 for rating */
const ratingSchema = z
  .union([z.string(), z.number()])
  .optional()
  .transform((v) => {
    if (v === undefined || v === null || v === '') return undefined;
    const n = typeof v === 'string' ? parseInt(v, 10) : v;
    if (Number.isNaN(n)) return undefined;
    return Math.min(5, Math.max(1, n)) as number;
  });

export const feedbackImportSchema = z.object({
  content: z.string().min(1, 'Content is required'),
  contributor: z.string().optional().default(''),
  source: z.string().optional().default('import'),
  client: z.string().optional(),
  client_name: z.string().optional(),
  customer: z.string().optional(),
  sentiment: sentimentSchema,
  status: z.string().optional().default('New'),
  priority: feedbackPrioritySchema,
  product_area: z.string().optional().default(''),
  rating: ratingSchema,
});

export type FeedbackImportRow = z.infer<typeof feedbackImportSchema>;

export function getClientNameFromRow(row: FeedbackImportRow): string | undefined {
  const name = (row.client ?? row.client_name ?? row.customer ?? '').trim();
  return name || undefined;
}

const ideaStatusSchema = z
  .string()
  .optional()
  .transform((v) => v?.trim() || 'New')
  .default('New');

const scoreSchema = (defaultVal: number) =>
  z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => {
      if (v === undefined || v === null || v === '') return defaultVal;
      const n = typeof v === 'string' ? parseInt(v, 10) : v;
      return Number.isNaN(n) ? defaultVal : Math.min(5, Math.max(1, n)) as 1 | 2 | 3 | 4 | 5;
    });

const optionalNumberSchema = z
  .union([z.string(), z.number()])
  .optional()
  .transform((v) => {
    if (v === undefined || v === null || v === '') return undefined;
    const n = typeof v === 'string' ? parseInt(v, 10) : v;
    return Number.isNaN(n) ? undefined : n;
  });

export const ideaImportSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional().default(''),
  status: ideaStatusSchema,
  priority: prioritySchema,
  effort: scoreSchema(3),
  impact: scoreSchema(3),
  reach: optionalNumberSchema,
  confidence: optionalNumberSchema,
  time_criticality: optionalNumberSchema,
  risk_reduction: optionalNumberSchema,
  category: z.string().optional().default(''),
  assigned_to: z.string().optional().default(''),
  target_quarter: z.string().optional().default(''),
});

export type IdeaImportRow = z.infer<typeof ideaImportSchema>;

export const FEEDBACK_IMPORT_TEMPLATE =
  'customer,source,contributor,content,sentiment,status,priority,product_area,rating\n"Acme Corp",import,Jane Smith,"Customer loves the new dashboard, wants dark mode option",positive,New,4,UI/UX,5';

export const IDEAS_IMPORT_TEMPLATE =
  'title,description,status,priority,effort,impact,reach,category,target_quarter\n"Add dark mode","Implement theme toggle with persistent user preference",New,4,3,5,150,Features,Q2 2025';
